'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { track } from '@/lib/analytics';

interface UserProfile {
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

interface AuthContextValue {
  user: any;
  session: any;
  accessToken: string | null;
  loading: boolean;
  profile: UserProfile | null;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Returns true if we should redirect an unauthenticated user.
 * Waits for auth to fully resolve AND checks that there's no
 * localStorage flag indicating the user was previously authenticated
 * (which would mean auth is still resolving / token refresh in progress).
 */
export function useRequireAuth(): { shouldRedirect: boolean; loading: boolean } {
  const { user, loading } = useAuth();
  const [wasAuth] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('smt_auth_active') === '1';
  });

  // Only redirect if:
  // 1. Auth has finished loading
  // 2. There is no user
  // 3. The user was NOT previously authenticated (no localStorage flag)
  const shouldRedirect = !loading && !user && !wasAuth;

  return { shouldRedirect, loading: loading || (wasAuth && !user) };
}

async function fetchUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const { data } = await supabase
      .from('User')
      .select('username, displayName, image')
      .eq('id', userId)
      .single();

    if (!data) return null;

    return {
      username: data.username || '',
      displayName: data.displayName || data.username || '',
      avatarUrl: data.image || null,
    };
  } catch {
    return null;
  }
}

const AUTH_FLAG_KEY = 'smt_auth_active';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const initializedRef = useRef(false);
  const attributedRef = useRef(false);

  // If this browser arrived via a /i/<code> share link (smt_signal_ref cookie) and a user is now
  // signed in, attribute the (new) signup to the promoter. Idempotent + no-op for veterans.
  function maybeAttributeReferral(sess: any) {
    if (attributedRef.current || typeof document === 'undefined') return;
    if (!/(?:^|;\s*)smt_signal_ref=/.test(document.cookie)) return;
    attributedRef.current = true;
    const token = sess?.access_token;
    fetch('/api/signal/attribute', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    }).catch(() => {});
  }

  useEffect(() => {
    let mounted = true;

    async function initSession() {
      try {
        // getSession() reads from local storage / cookies synchronously-ish
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (!mounted) return;

        if (existingSession?.user) {
          // Validate the session server-side to ensure the token is still valid
          const { data: { user: validatedUser } } = await supabase.auth.getUser();

          if (!mounted) return;

          if (validatedUser) {
            setSession(existingSession);
            setUser(validatedUser);
            if (typeof window !== 'undefined') {
              localStorage.setItem(AUTH_FLAG_KEY, '1');
            }
            maybeAttributeReferral(existingSession);
            fetchUserProfile(validatedUser.id).then((p) => {
              if (mounted) setProfile(p);
            });
          } else {
            setSession(null);
            setUser(null);
            setProfile(null);
            if (typeof window !== 'undefined') {
              localStorage.removeItem(AUTH_FLAG_KEY);
            }
          }
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          if (typeof window !== 'undefined') {
            localStorage.removeItem(AUTH_FLAG_KEY);
          }
        }
      } catch {
        if (mounted) {
          setSession(null);
          setUser(null);
          setProfile(null);
          if (typeof window !== 'undefined') {
            localStorage.removeItem(AUTH_FLAG_KEY);
          }
        }
      } finally {
        if (mounted && !initializedRef.current) {
          initializedRef.current = true;
          setLoading(false);
        }
      }
    }

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;

        if (event === 'INITIAL_SESSION') {
          if (!initializedRef.current) {
            setSession(newSession);
            setUser(newSession?.user ?? null);
            if (newSession?.user) {
              if (typeof window !== 'undefined') {
                localStorage.setItem(AUTH_FLAG_KEY, '1');
              }
              maybeAttributeReferral(newSession);
              fetchUserProfile(newSession.user.id).then((p) => {
                if (mounted) setProfile(p);
              });
            } else {
              setProfile(null);
              if (typeof window !== 'undefined') {
                localStorage.removeItem(AUTH_FLAG_KEY);
              }
            }
            initializedRef.current = true;
            setLoading(false);
          }
          return;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          if (typeof window !== 'undefined') {
            localStorage.setItem(AUTH_FLAG_KEY, '1');
          }
          maybeAttributeReferral(newSession);
          fetchUserProfile(newSession.user.id).then((p) => {
            if (mounted) setProfile(p);
          });
        } else {
          setProfile(null);
          if (typeof window !== 'undefined') {
            localStorage.removeItem(AUTH_FLAG_KEY);
          }
        }
      }
    );

    // Safety fallback: clear loading after 5s if nothing has resolved
    const fallbackTimer = setTimeout(() => {
      if (mounted && !initializedRef.current) {
        initializedRef.current = true;
        setLoading(false);
      }
    }, 5000);

    return () => {
      mounted = false;
      clearTimeout(fallbackTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    track('sign_up', { method: 'password' });
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    track('login', { method: 'password' });
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(AUTH_FLAG_KEY);
    }
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('User')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value: AuthContextValue = {
    user,
    session,
    accessToken: session?.access_token ?? null,
    loading,
    profile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
